import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CalendarDatepickerComponent } from "../calendar-datepicker.component";
import { CalendarFilterSecEventsComponent } from "../calendar-filter-sec-events/calendar-filter-sec-events.component";
import { CalendarService } from "../../../domain/calendar-service";
import { isBroadcastPresetShowLike, Show } from "../../../domain/show";
import moment from "moment";
import { Channel } from "../../../domain/channel";
import { ToastrService } from "ngx-toastr";
import { ShowRepository } from "../../../domain/show-repository";
import { ICommonUtilInterface } from "../../../domain/common/common-util.interface";
import { UserService } from "../../../domain/user-service";
import { CalendarGridColumnsCustomizationComponent } from "../calendar-grid-columns-customization/calendar-grid-columns-customization.component";

@Component({
  selector: "icn-calendar-header",
  templateUrl: "./calendar-header.component.html",
  styleUrls: ["./calendar-header.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarHeaderComponent implements OnDestroy, OnChanges, OnInit {
  constructor(
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private calendarService: CalendarService,
    private showsRepository: ShowRepository,
    private _toast: ToastrService,
    private _commonUtils: ICommonUtilInterface,
    private _userService: UserService,
  ) {}

  @ViewChild(CalendarDatepickerComponent)
  datePicker: CalendarDatepickerComponent;
  @ViewChild(CalendarFilterSecEventsComponent)
  secEventsFilter: CalendarFilterSecEventsComponent;
  @ViewChild(CalendarGridColumnsCustomizationComponent)
  columnsCustomization: CalendarGridColumnsCustomizationComponent;

  @Input() title: string;
  @Input() activeView: string;
  @Input() clinetId: string;
  @Input() currentDate;
  @Input() isGridDisplay;
  @Input() currentShow: Show;
  @Input() nextShows: Show[];
  @Input() currentChannel: Channel;
  @Input() lockNow: boolean;
  @Output() now = new EventEmitter();
  @Output() prev = new EventEmitter();
  @Output() next = new EventEmitter();
  @Output() setViewTypeAndTime = new EventEmitter<{
    viewType: string;
    time: moment.Moment;
  }>();
  @Output() setCurrentDate = new EventEmitter();
  @Output() viewOptionChange = new EventEmitter<string>();
  @Output() showToSkipToChanged = new EventEmitter<{ show: Show }>();

  public showToSkipTo: Show = null;
  private emptyDuration: moment.Duration = moment.duration(0, "minutes");

  minimizedRibbon = false;
  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.calendarService.clearCache();
  }

  showDatepicker(): void {
    this.datePicker.show();
  }

  hideDatepicker() {
    this.datePicker.hide();
  }

  onClickedOutside() {
    this.datePicker.hide();
  }

  onDatepickerApply(newDate) {
    this.hideDatepicker();
    // this.activeView
    this.setCurrentDate.emit(newDate);
  }

  changeViewType(viewType: string) {
    if (this.activeView === viewType) {
      return;
    }

    this.setViewTypeAndTime.emit({
      viewType: viewType,
      time: this.lockNow ? moment() : null,
    });
  }

  getCurrentShowName(): string {
    return !this.currentShow ? `no current show` : this.currentShow.name;
  }

  getShowDuration(type: string): string {
    let duration = this.emptyDuration;

    switch (type) {
      case "elapsed":
        if (this.currentShow) {
          duration = this.calendarService.getCurrentShowElapsedTime();
        }
        break;
      case "remaining":
        if (this.currentShow) {
          duration = this.calendarService.getCurrentShowRemainingTime();
        }
        break;
      case "next":
        if (this.showToSkipTo && this.calendarService.nextShows.length > 0) {
          duration = this.showToSkipTo.duration;
        }
        break;
    }
    return moment.utc(duration.asMilliseconds()).format("HH:mm:ss");
  }

  setShowToSkipTo(value) {
    if (value.data && value.data.length > 0) {
      const show = value.data[0];
      if (isBroadcastPresetShowLike(show)) {
        return;
      }
      this.showToSkipTo = show;
      this.showToSkipToChanged.emit({ show: this.showToSkipTo });
    }
  }

  skipCurrentShow(): void {
    if (this.currentChannel.isActive) {
      if (
        this.showToSkipTo &&
        (!this.currentShow || this.showToSkipTo.id !== this.currentShow.id) &&
        !this.calendarService.DuringSkip
      ) {
        const showId = this.showToSkipTo.id;
        console.log("skipCurrentShow->calendarService.skipCurrentShow", showId);
        this.showToSkipTo = null;
        this.calendarService.skipCurrentShow(showId, this.clinetId).then();
      }
    } else {
      this._toast.error(
        `Please activate the channel in order to perform the skip`,
      );
    }
  }

  skipToShowDescriptionText(): string {
    if (
      !this.nextShows ||
      this.nextShows.length === 0 ||
      this.nextShows[0] === this.showToSkipTo
    ) {
      return "Next Show";
    }
    return "Selected Show";
  }

  DuringSkipToShow() {
    return this.calendarService.DuringSkip;
  }

  minimizeRibbonToggle() {
    this.minimizedRibbon = !this.minimizedRibbon;
  }

  onViewOptionChange(option: string): void {
    this.viewOptionChange.emit(option);
  }

  showSecEventsFilter(): void {
    this.secEventsFilter.toggle();
    this.changeDetectorRef.detectChanges();
  }

  onClickedOutsideSecEventsFilter() {
    if (this.secEventsFilter.active) {
      this.secEventsFilter.hide();
      this.changeDetectorRef.detectChanges();
    }
  }
  showColumnsCustomization(): void {
    this.columnsCustomization.toggle();
    this.changeDetectorRef.detectChanges();
  }

  onClickedOutsideColumnsCustomization() {
    if (this.columnsCustomization.active) {
      this.columnsCustomization.hide();
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["currentShow"] &&
      this.showToSkipTo &&
      changes["currentShow"].currentValue &&
      changes["currentShow"].currentValue.id === this.showToSkipTo.id &&
      this.currentChannel
    ) {
      console.log(
        "calendar-header->ngOnChanges->changes['currentShow']",
        changes["currentShow"].currentValue,
        this.showToSkipTo,
      );
      /*this.showsRepository.updatePlayout(this.currentChannel.id).then(() => {
        this.showToSkipTo = null;
      });*/
    }
  }

  protected clearPrerollsClicked() {
    this.showToSkipTo = null;

    this.showsRepository.clearChannelPrerollList(this.currentChannel.id).then(() => {
      this._toast.success("Preroll list cleared successfully");
    }).catch(error => {
      this._toast.error(error.message);
    });
  }
}
